const TELEGRAM_MINI_APP_LAUNCH_PARAM_NAMES = [
  'tgWebAppData',
  'tgWebAppVersion',
  'tgWebAppPlatform',
  'tgWebAppThemeParams',
  'tgWebAppDefaultColors',
  'tgWebAppFullscreen',
  'tgWebAppShowSettings',
]
const TG_VALID_INIT_DATA_KEY = 'tgValidInitData'
const TG_MINI_APP_AUTH_EVENT_KEY = 'tgMiniAppAuthEvent'
const TELEGRAM_MINI_APP_CLASS = 'telegram-mini-app'

function readSessionStorage(key) {
  try {
    return sessionStorage.getItem(key)
  } catch {
    return null
  }
}

function writeSessionStorage(key, value) {
  try {
    sessionStorage.setItem(key, value)
  } catch {
    // sessionStorage can be unavailable in restricted browser modes.
  }
}

function removeSessionStorage(key) {
  try {
    sessionStorage.removeItem(key)
  } catch {
    // sessionStorage can be unavailable in restricted browser modes.
  }
}

function normalizeErrorData(data) {
  if (!data) {
    return null
  }

  if (typeof data === 'string') {
    return data
  }

  try {
    return JSON.parse(JSON.stringify(data))
  } catch {
    return String(data)
  }
}

export function getTelegramMiniApp() {
  return window.Telegram?.WebApp || null
}

function getTelegramMiniAppRuntimeInitData() {
  return getTelegramMiniApp()?.initData || ''
}

function getUrlSearchParams(urlPart) {
  const normalizedUrlPart = urlPart.replace(/^[#?]/, '')
  const query = normalizedUrlPart.includes('?')
    ? normalizedUrlPart.slice(normalizedUrlPart.indexOf('?') + 1)
    : normalizedUrlPart

  return new URLSearchParams(query)
}

function hasTelegramMiniAppParams(urlPart) {
  const params = getUrlSearchParams(urlPart)

  return TELEGRAM_MINI_APP_LAUNCH_PARAM_NAMES.some((param) => params.has(param))
}

export function hasTelegramMiniAppLaunchParams() {
  return hasTelegramMiniAppParams(location.search) || hasTelegramMiniAppParams(location.hash)
}

export function getStoredTelegramMiniAppInitData() {
  return readSessionStorage(TG_VALID_INIT_DATA_KEY) || ''
}

export function hasTelegramMiniAppInitDataHash(initData) {
  if (!initData || typeof initData !== 'string') {
    return false
  }

  try {
    return Boolean(getUrlSearchParams(initData).get('hash'))
  } catch {
    return false
  }
}

export function setStoredTelegramMiniAppInitData(initData) {
  const storedInitData = getStoredTelegramMiniAppInitData()

  if (storedInitData) {
    return storedInitData
  }

  if (!hasTelegramMiniAppInitDataHash(initData)) {
    return ''
  }

  writeSessionStorage(TG_VALID_INIT_DATA_KEY, initData)
  return initData
}

export function hasTelegramMiniAppRuntime() {
  return Boolean(
    getTelegramMiniAppRuntimeInitData() || getStoredTelegramMiniAppInitData() || hasTelegramMiniAppLaunchParams(),
  )
}

export function getTelegramMiniAppInitData({ fromStorage = true, preferStorage = false } = {}) {
  const storedInitData = fromStorage ? getStoredTelegramMiniAppInitData() : ''

  if (storedInitData) {
    return storedInitData
  }

  const initData = getTelegramMiniAppRuntimeInitData()
  const savedInitData = initData ? setStoredTelegramMiniAppInitData(initData) : ''

  if (preferStorage) {
    return savedInitData
  }

  return initData || savedInitData
}

export function markTelegramMiniAppDocument() {
  document.documentElement.classList.add(TELEGRAM_MINI_APP_CLASS)
}

export function applyTelegramMiniAppSettings() {
  const telegramMiniApp = getTelegramMiniApp()

  if (!telegramMiniApp) {
    return false
  }

  telegramMiniApp.isClosingConfirmationEnabled = true
  telegramMiniApp.isVerticalSwipesEnabled = false
  markTelegramMiniAppDocument()

  return true
}

export function normalizeTelegramMiniAppAuthError(error) {
  const status = error?.response?.status ?? error?.status ?? null
  const statusText = error?.response?.statusText ?? error?.statusText ?? ''
  const data = normalizeErrorData(error?.response?.data ?? error?.data)
  const message = error?.message || statusText || 'Telegram Mini App auth error'

  return {
    data,
    message,
    status,
    statusText,
  }
}

export function markTelegramMiniAppAuthSuccess({ emitEvent = true, initData } = {}) {
  if (initData) {
    setStoredTelegramMiniAppInitData(initData)
  }

  if (emitEvent) {
    writeSessionStorage(TG_MINI_APP_AUTH_EVENT_KEY, 'true')
  }
}

export function consumeTelegramMiniAppAuthEvent() {
  const hasAuthEvent = readSessionStorage(TG_MINI_APP_AUTH_EVENT_KEY) === 'true'

  removeSessionStorage(TG_MINI_APP_AUTH_EVENT_KEY)
  return hasAuthEvent
}
